import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Users, MessageSquare, Globe, Award, Rocket, GitBranch, Share2, 
  Search, User, Zap, Bell, TrendingUp, ArrowRight, ThumbsUp, UserPlus,
  BookOpen, MessageCircle, Heart, Calendar, Send, Plus, FileText,
  CheckCircle, Clock, Settings, Smile, Image, Paperclip, Hash, 
  AlertCircle, Crown, Medal, Shield, Star, Trophy, Smartphone,
  BarChart2, ChevronDown, PenTool, Trash2, Link, Eye, EyeOff, Volume2, Volume, Mail
} from 'lucide-react';
import supabase, { adminSupabase } from '../services/supabaseService';
import { userService } from '../services/userService';
import { useWalletUser } from '../hooks/useWalletUser';
import { useChatMessages } from '../hooks/useChatMessages';
import { ChatMessage } from '../services/chatService';
import { getUserProfile } from '../services/userProfileService';
import type { UserProfile } from '../services/userProfileService';

// TypeScript declarations for wallet providers
declare global {
  interface Window {
    solana?: {
      isConnected: boolean;
      publicKey: {
        toString(): string;
      };
    };
    solflare?: {
      isConnected: boolean;
      publicKey: {
        toString(): string;
      };
    };
  }
}

// Custom scrollbar styles for Community component
const communityStyles = `
  /* Custom scrollbar styling */
  .community-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .community-scrollbar::-webkit-scrollbar-track {
    background: rgba(30, 40, 40, 0.2);
    border-radius: 3px;
  }

  .community-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(71, 190, 143, 0.3);
    border-radius: 3px;
    transition: all 0.2s;
  }

  .community-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(71, 190, 143, 0.5);
  }
  
  /* Section styles with solid backgrounds */
  .community-card {
    background: #182828;
    border: 1px solid rgba(71, 190, 143, 0.2);
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  .community-card-header {
    background: #1a2a2a;
    border-bottom: 1px solid rgba(71, 190, 143, 0.2);
    padding: 0.75rem 1rem;
  }
  
  .community-card-body {
    padding: 1rem;
  }
  
  /* Item styling */
  .community-item {
    background: #1c2c2c;
    margin-bottom: 1px;
    border-radius: 6px;
    transition: all 0.2s;
  }
  
  .community-item:hover {
    background: #243434;
  }
  
  /* Live Chat Styles */
  .chat-container {
    display: flex;
    flex-direction: column;
    height: 550px;
    position: relative;
  }
  
  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
  }
  
  .chat-message {
    margin-bottom: 10px;
    position: relative;
    width: 100%;
  }
  
  .chat-message-content {
    display: grid;
    grid-template-columns: 38px 1fr;
    grid-template-areas: 
      "avatar header"
      "avatar content";
    gap: 8px;
    padding: 8px;
    background: rgba(36, 52, 52, 0.8);
    border-radius: 8px;
    border: 1px solid rgba(71, 190, 143, 0.1);
    position: relative;
  }
  
  .chat-message-avatar {
    grid-area: avatar;
    width: 38px;
    height: 38px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid rgba(71, 190, 143, 0.2);
  }
  
  .chat-message-header {
    grid-area: header;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 4px;
    min-height: 24px;
    position: relative;
  }
  
  .user-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0;
    height: 100%;
  }
  
  .chat-username {
    font-weight: 600;
    font-size: 0.85rem;
    color: #42ae84;
    margin-right: 3px;
    display: inline-flex;
    align-items: center;
    line-height: 1;
    height: 18px;
  }
  
  .timestamp {
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.4);
    white-space: nowrap;
    margin-left: auto;
    flex-shrink: 0;
  }
  
  .chat-message-body {
    grid-area: content;
    padding-top: 0;
  }
  
  .message-text {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.4;
    word-break: break-word;
  }
  
  .wallet-address {
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.65);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 110px;
    padding: 0 6px;
    background: rgba(71, 190, 143, 0.05);
    backdrop-filter: blur(5px);
    border: 1px solid rgba(71, 190, 143, 0.15);
    border-radius: 12px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 2px;
    margin: 0 2px 0 0;
    line-height: 1;
    vertical-align: middle;
    box-sizing: border-box;
  }

  .social-icon {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.15s;
    cursor: pointer;
    padding: 2px;
    margin-left: 1px;
    line-height: 1;
    vertical-align: middle;
    box-sizing: border-box;
  }
  
  .social-icon.active:hover {
    opacity: 1;
    transform: scale(1.1);
  }
  
  .social-icon.inactive {
    opacity: 0.3;
    cursor: default;
  }
  
  .social-icon.inactive:hover {
    opacity: 0.3;
    transform: none;
  }
  
  .twitter-icon {
    color: #1DA1F2;
    background: rgba(29, 161, 242, 0.1);
  }
  
  .telegram-icon {
    color: #0088cc;
    background: rgba(0, 136, 204, 0.1);
  }
  
  .discord-icon {
    color: #7289DA;
    background: rgba(114, 137, 218, 0.1);
  }
  
  .copy-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    opacity: 0.6;
    cursor: pointer;
    transition: all 0.15s;
    background: transparent;
    border: none;
    padding: 0;
    margin: 0 0 0 4px;
    flex-shrink: 0;
    line-height: 1;
    vertical-align: middle;
    box-sizing: border-box;
  }
  
  .copy-button:hover {
    opacity: 1;
  }
  
  .copy-icon {
    width: 10px;
    height: 10px;
    color: rgba(71, 190, 143, 0.8);
  }
  
  .check-icon {
    width: 10px;
    height: 10px;
    color: #42ae84;
  }
  
  .chat-input-container {
    border-top: 1px solid rgba(71, 190, 143, 0.15);
    padding: 0.75rem;
    background: #1a2a2a;
  }
  
  .chat-input-wrapper {
    display: flex;
    align-items: center;
    background: #243434;
    border-radius: 6px;
    padding: 0.25rem 0.5rem;
    border: 1px solid rgba(71, 190, 143, 0.2);
  }
  
  .chat-input {
    flex: 1;
    background: transparent;
    border: none;
    padding: 0.5rem;
    color: white;
    font-size: 0.875rem;
    outline: none;
  }
  
  .chat-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    position: relative;
  }
  
  .chat-action-btn {
    padding: 0.5rem;
    border-radius: 4px;
    color: rgba(255, 255, 255, 0.6);
    transition: all 0.2s;
    cursor: pointer;
  }
  
  .chat-action-btn:hover {
    background: rgba(71, 190, 143, 0.1);
    color: rgba(255, 255, 255, 0.9);
  }
  
  .emoji-picker-container {
    position: absolute;
    bottom: 100%;
    right: 0;
    z-index: 10;
    margin-bottom: 8px;
    box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.2);
  }
  
  .chat-settings-dropdown {
    position: fixed;
    width: 520px;
    background: #1a2a2a;
    border: 1px solid rgba(71, 190, 143, 0.3);
    border-radius: 6px;
    box-shadow: 0 -8px 16px rgba(0, 0, 0, 0.4);
    z-index: 1000;
    overflow: hidden;
    opacity: 0;
    transform: translateY(10px);
    transition: opacity 0.15s ease, transform 0.15s ease;
    bottom: 68px; /* Position it above the chat input area */
    right: 20px; /* Position from right edge */
  }
  
  .chat-settings-dropdown.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  .chat-settings-section {
    padding: 0.75rem;
    border-bottom: 1px solid rgba(71, 190, 143, 0.1);
  }
  
  .chat-settings-section:last-child {
    border-bottom: none;
  }
  
  .chat-settings-header {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(71, 190, 143, 0.9);
    margin-bottom: 0.5rem;
    text-transform: uppercase;
  }
  
  .chat-settings-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0;
  }
  
  .chat-settings-label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.8);
  }
  
  /* Chat Messages */
  .system-message {
    text-align: center;
    padding: 0.5rem;
    margin: 0.5rem 0;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
    background: rgba(71, 190, 143, 0.05);
    border-radius: 4px;
  }
  
  /* Color picker */
  .color-option {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    cursor: pointer;
    transition: transform 0.2s;
  }
  
  .color-option:hover {
    transform: scale(1.1);
  }
  
  .color-option.selected {
    border: 2px solid white;
  }
  
  /* Leaderboard styles */
  .leaderboard-tab {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .leaderboard-tab.active {
    background: rgba(71, 190, 143, 0.2);
    color: #42ae84;
  }
  
  .leaderboard-item {
    display: flex;
    align-items: center;
    padding: 0.75rem;
    border-radius: 6px;
    transition: all 0.2s;
    margin-bottom: 0.5rem;
    background: #1c2c2c;
    border: 1px solid rgba(71, 190, 143, 0.05);
  }
  
  .leaderboard-item:hover {
    background: #243434;
  }
  
  .leaderboard-rank {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.8rem;
    border-radius: 50%;
  }
  
  .rank-1 {
    background: linear-gradient(135deg, #ffd700, #ffa600);
    color: #000;
  }
  
  .rank-2 {
    background: linear-gradient(135deg, #e6e6e6, #c0c0c0);
    color: #000;
  }
  
  .rank-3 {
    background: linear-gradient(135deg, #cd7f32, #a04000);
    color: #fff;
  }
  
  .rank-other {
    background: #243434;
    color: #fff;
  }
  
  .level-badge {
    background: linear-gradient(135deg, rgba(71, 190, 143, 0.3), rgba(71, 190, 143, 0.1));
    border: 1px solid rgba(71, 190, 143, 0.2);
    padding: 0.15rem 0.4rem;
    font-size: 0.65rem;
    border-radius: 3px;
    font-weight: 600;
    color: #42ae84;
  }
  
  .progress-bar {
    height: 6px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    overflow: hidden;
  }
  
  .progress-fill {
    height: 100%;
    background: #42ae84;
  }
  
  .trophy-card {
    padding: 0.75rem;
    border-radius: 8px;
    text-align: center;
    background: linear-gradient(135deg, rgba(71, 190, 143, 0.1), rgba(71, 190, 143, 0.05));
    border: 1px solid rgba(71, 190, 143, 0.1);
    position: relative;
    overflow: hidden;
  }
  
  .trophy-icon {
    margin: 0 auto 0.5rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(71, 190, 143, 0.1);
    border-radius: 50%;
  }
  
  .trophy-locked {
    filter: grayscale(100%);
    opacity: 0.5;
  }
  
  .trophy-locked:after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
  }
  
  .achievement-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 4px;
    background: #42ae84;
  }
`;

// Add state variable and interface for social media details
interface UserSocialMedia {
  twitter_handle?: string | null;
  telegram_handle?: string | null;
  discord_handle?: string | null;
}

// Update the ExtendedChatMessage interface to include our custom fields
interface ExtendedChatMessage extends ChatMessage {
  share_wallet_with_others?: boolean;
  share_social_links?: boolean;
}

// Update the ChatPreferences interface with our new properties
interface ChatPreferences {
  display_wallet_address: boolean;
  display_own_wallet_address: boolean;
  display_others_wallet_address: boolean;
  user_color: string;
  notification_sound: boolean;
  email_notifications: boolean;
  user_status: 'online' | 'away' | 'busy';
  share_wallet_with_others?: boolean;
  share_social_links?: boolean;
}

// Add CSS styles for the 2-column settings layout
const chatSettingsStyles = `
  .settings-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  
  .settings-column {
    max-width: 250px;
  }
  
  .settings-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(4px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }
  
  .chat-settings-dropdown {
    position: relative;
    width: 520px;
    background: #1a2a2a;
    border: 2px solid rgba(71, 190, 143, 0.5);
    border-radius: 8px;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.8), 0 0 15px rgba(71, 190, 143, 0.3);
    overflow-y: auto;
    max-height: 80vh;
    animation: modalAppear 0.3s forwards;
  }
  
  @keyframes modalAppear {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const Community: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'feed' | 'members' | 'events'>('feed');
  const connectionsRef = useRef<HTMLDivElement>(null);
  
  // Chat state
  const [chatMessage, setChatMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showChatSettings, setShowChatSettings] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // Load user data with the useWalletUser hook
  const currentUserData = useWalletUser();
  
  // Use our chat messages hook
  const {
    messages: chatMessages,
    isLoading: isLoadingChat,
    error: chatError,
    sendMessage,
    loadMoreMessages,
    preferences: chatPreferences,
    updatePreferences,
    setUserStatus
  } = useChatMessages(
    currentUserData.walletAddress,
    currentUserData.walletProvider,
    currentUserData.name,
    currentUserData.avatar
  );
  
  // Add setMessages for local state manipulation
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  
  // Track deleted message IDs to prevent them from reappearing
  const [deletedMessageIds, setDeletedMessageIds] = useState<Set<string>>(new Set());
  
  // Sync local messages with the hook-provided messages, filtering out deleted ones
  useEffect(() => {
    const filteredMessages = chatMessages.filter(msg => !deletedMessageIds.has(msg.id));
    setLocalMessages(filteredMessages);
  }, [chatMessages, deletedMessageIds]);

  // Parse preferences into state variables for UI
  const [displayWalletAddress, setDisplayWalletAddress] = useState(true);
  const [displayOwnWalletAddress, setDisplayOwnWalletAddress] = useState(true);
  const [displayOthersWalletAddress, setDisplayOthersWalletAddress] = useState(true);
  // Add social media privacy settings
  const [shareWalletWithOthers, setShareWalletWithOthers] = useState(true);
  const [shareSocialLinks, setShareSocialLinks] = useState(true);
  const [userColor, setUserColor] = useState('#42ae84');
  const [notificationSound, setNotificationSound] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [userStatusLocal, setUserStatusLocal] = useState<'online' | 'away' | 'busy'>('online');
  const chatInputRef = useRef<HTMLInputElement>(null);
  const chatSettingsRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  // Update local state when preferences change
  useEffect(() => {
    if (chatPreferences) {
      setDisplayWalletAddress(chatPreferences.display_wallet_address);
      setDisplayOwnWalletAddress(chatPreferences.display_own_wallet_address);
      setDisplayOthersWalletAddress(chatPreferences.display_others_wallet_address);
      setUserColor(chatPreferences.user_color);
      setNotificationSound(chatPreferences.notification_sound);
      setEmailNotifications(chatPreferences.email_notifications);
      setUserStatusLocal(chatPreferences.user_status);
      
      // If we have custom preferences in localStorage, use those instead
      const storedPrivacy = localStorage.getItem('ekko_privacy_settings');
      if (storedPrivacy) {
        try {
          const { shareWallet, shareSocial } = JSON.parse(storedPrivacy);
          setShareWalletWithOthers(shareWallet);
          setShareSocialLinks(shareSocial);
        } catch (err) {
          console.error('Error parsing stored privacy settings', err);
        }
      }
    }
  }, [chatPreferences]);

  // Handle clicks outside the settings panel to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Close settings panel when clicking outside
      if (chatSettingsRef.current && !chatSettingsRef.current.contains(event.target as Node)) {
        setShowChatSettings(false);
      }
      
      // Close emoji picker when clicking outside
      if (
        showEmojiPicker && 
        emojiPickerRef.current && 
        !emojiPickerRef.current.contains(event.target as Node) &&
        // Don't close if clicking the emoji toggle button itself
        !(event.target as Element).closest('.chat-action-btn')
      ) {
        setShowEmojiPicker(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Debug current user data
  useEffect(() => {
  }, [currentUserData]);
  
  const handleTabClick = (tab: 'feed' | 'members' | 'events') => {
    setActiveTab(tab);
  };
  
  // Leaderboard state
  const [activeLeaderboard, setActiveLeaderboard] = useState<'trading' | 'community' | 'achievements'>('trading');
  const [showAchievements, setShowAchievements] = useState(false);
  
  // Calculate level progress utility function
  const calculateLevelProgress = (xp: number, nextLevelXp: number, prevLevelXp = 0) => {
    const progress = ((xp - prevLevelXp) / (nextLevelXp - prevLevelXp)) * 100;
    return Math.min(Math.max(progress, 0), 100); // Ensure it's between 0-100
  };
  
  // Update function signatures and implementation to work with our actual API
  // Fix the handleSendMessage function to work with the actual sendMessage signature
  const handleSendMessage = () => {
    if (!chatMessage.trim()) return;
    
    // Add privacy metadata if needed
    let messageToSend = chatMessage.trim();
    if (!shareWalletWithOthers || !shareSocialLinks) {
      messageToSend += `||PRIVACY::${shareWalletWithOthers ? '1' : '0'}|${shareSocialLinks ? '1' : '0'}`;
    }
    
    // Send message
    sendMessage(messageToSend);
    setChatMessage('');
    
    // Focus and scroll
    setTimeout(() => {
      if (chatInputRef.current) {
        chatInputRef.current.focus();
      }
      
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 100);
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const formatMessageTime = (timestamp: string) => {
    const messageDate = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - messageDate.getTime()) / 1000);
    
    // Within the last minute
    if (diffInSeconds < 60) {
      return 'Just now';
    }
    
    // Within the last hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    // Within the last day
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    
    // Default: show time
    return messageDate.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };
  
  const toggleEmojiPicker = () => {
    setShowEmojiPicker(prev => !prev);
    // Close settings if open
    if (showChatSettings) setShowChatSettings(false);
  };
  
  const handleEmojiSelect = (emoji: string) => {
    setChatMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };
  
  // Add this new useEffect to handle positioning of the settings panel when it's opened
  useEffect(() => {
    if (showChatSettings && chatSettingsRef.current) {
      // Get the settings button element
      const settingsButton = document.querySelector('.chat-action-btn:has(svg[data-lucide="Settings"])');
      if (settingsButton instanceof HTMLElement) {
        const buttonRect = settingsButton.getBoundingClientRect();
        const settingsPanel = chatSettingsRef.current;
        
        // Position the panel
        settingsPanel.style.top = `${buttonRect.top - settingsPanel.offsetHeight - 10}px`;
        
        // Center the panel or align it with the right edge of the chat container
        const chatContainer = document.querySelector('.col-span-2');
        if (chatContainer instanceof HTMLElement) {
          const containerRect = chatContainer.getBoundingClientRect();
          settingsPanel.style.left = `${containerRect.right - settingsPanel.offsetWidth - 20}px`;
        } else {
          // Fallback positioning if container not found
          settingsPanel.style.left = `${Math.max(20, buttonRect.left - settingsPanel.offsetWidth/2 + buttonRect.width/2)}px`;
        }
      }
    }
  }, [showChatSettings]);

  const toggleChatSettings = () => {
    setShowChatSettings(prev => !prev);
    // Close emoji picker if open
    if (showEmojiPicker) setShowEmojiPicker(false);
  };
  
  const handleSettingsClick = (e: React.MouseEvent) => {
    // Prevent event from bubbling up and closing the settings
    e.stopPropagation();
  };
  
  const handleColorSelect = (color: string) => {
    setUserColor(color);
    updatePreferences({ user_color: color });
  };
  
  const handleUserStatusChange = (status: 'online' | 'away' | 'busy') => {
    setUserStatusLocal(status);
    setUserStatus(status);
  };

  const handleWalletDisplayToggle = () => {
    const newValue = !displayWalletAddress;
    setDisplayWalletAddress(newValue);
    setDisplayOwnWalletAddress(newValue);
    setDisplayOthersWalletAddress(newValue);
    updatePreferences({ 
      display_wallet_address: newValue,
      display_own_wallet_address: newValue,
      display_others_wallet_address: newValue
    });
  };

  const handleOwnWalletDisplayToggle = () => {
    const newValue = !displayOwnWalletAddress;
    setDisplayOwnWalletAddress(newValue);
    updatePreferences({ display_own_wallet_address: newValue });
  };

  const handleOthersWalletDisplayToggle = () => {
    const newValue = !displayOthersWalletAddress;
    setDisplayOthersWalletAddress(newValue);
    updatePreferences({ display_others_wallet_address: newValue });
  };

  const handleNotificationSoundToggle = () => {
    const newValue = !notificationSound;
    setNotificationSound(newValue);
    updatePreferences({ notification_sound: newValue });
  };

  const handleEmailNotificationsToggle = () => {
    const newValue = !emailNotifications;
    setEmailNotifications(newValue);
    updatePreferences({ email_notifications: newValue });
  };

  // Add handlers for the new privacy toggles
  const handleShareWalletToggle = () => {
    const newValue = !shareWalletWithOthers;
    setShareWalletWithOthers(newValue);
    savePrivacySettings(newValue, shareSocialLinks);
  };

  const handleShareSocialLinksToggle = () => {
    const newValue = !shareSocialLinks;
    setShareSocialLinks(newValue);
    savePrivacySettings(shareWalletWithOthers, newValue);
  };

  // Add a function to save privacy settings to localStorage
  const savePrivacySettings = (shareWallet: boolean, shareSocial: boolean) => {
    localStorage.setItem('ekko_privacy_settings', JSON.stringify({
      shareWallet,
      shareSocial
    }));
  };

  // Load the privacy settings from localStorage on component mount
  useEffect(() => {
    const storedPrivacy = localStorage.getItem('ekko_privacy_settings');
    if (storedPrivacy) {
      try {
        const { shareWallet, shareSocial } = JSON.parse(storedPrivacy);
        setShareWalletWithOthers(shareWallet !== false);
        setShareSocialLinks(shareSocial !== false);
      } catch (err) {
        console.error('Error parsing stored privacy settings', err);
      }
    }
  }, []);

  // Add state for showing checkmark icons
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [showCheckmarks, setShowCheckmarks] = useState<Record<string, boolean>>({});
  
  // Function to copy wallet address to clipboard
  const copyWalletAddress = (address: string, messageId: string) => {
    navigator.clipboard.writeText(address)
      .then(() => {
        // Show checkmark icon
        setShowCheckmarks(prev => ({
          ...prev,
          [messageId]: true
        }));
        
        // Hide after 2 seconds
        setTimeout(() => {
          setShowCheckmarks(prev => ({
            ...prev,
            [messageId]: false
          }));
        }, 2000);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  // Load more messages when scrolling to top
  const handleChatScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop } = chatContainerRef.current;
      
      // Load more when user scrolls near the top
      if (scrollTop < 50 && !isLoadingChat) {
        // Preserve scroll position when loading more messages
        const scrollHeight = chatContainerRef.current.scrollHeight;
        
        loadMoreMessages();
        
        // After loading, restore scroll position
        // Use requestAnimationFrame to wait for DOM update
        requestAnimationFrame(() => {
          if (chatContainerRef.current) {
            const newScrollHeight = chatContainerRef.current.scrollHeight;
            const heightDifference = newScrollHeight - scrollHeight;
            chatContainerRef.current.scrollTop = heightDifference;
          }
        });
      }
    }
  };
  
  // Auto-scroll chat to bottom when new messages arrive, but only for new messages, not loaded history
  useEffect(() => {
    // Only auto-scroll if we're not loading more messages (scrolled near the bottom)
    if (chatContainerRef.current && !isLoadingChat) {
      const chatContainer = chatContainerRef.current;
      const isNearBottom = 
        chatContainer.scrollHeight - chatContainer.scrollTop - chatContainer.clientHeight < 100;
      
      // Only auto-scroll if we're already at the bottom
      if (isNearBottom) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }
  }, [localMessages, isLoadingChat]);
  
  // Mock leaderboard data
  const tradingLeaderboard: any[] = [];
  
  const communityLeaderboard: any[] = [];
  
  // Mock achievements data
  const achievements = [
    {
      id: 'ach1',
      name: 'Trading Expert',
      description: 'Complete 100 successful trades',
      icon: <Trophy className="text-yellow-500" />,
      progress: 100,
      total: 100,
      completed: true,
      reward: '500 XP + Gold Trophy',
    },
    {
      id: 'ach2',
      name: 'Community Guide',
      description: 'Help 50 community members with their questions',
      icon: <Shield className="text-blue-500" />,
      progress: 38,
      total: 50,
      completed: false,
      reward: '300 XP + Community Badge',
    },
    {
      id: 'ach3',
      name: 'Diamond Hands',
      description: 'Hold positions through high volatility for 30 days',
      icon: <Award className="text-purple-500" />,
      progress: 30,
      total: 30,
      completed: true,
      reward: '400 XP + Special Badge',
    },
    {
      id: 'ach4',
      name: 'Market Oracle',
      description: 'Successfully predict market direction 10 times',
      icon: <Star className="text-orange-500" />,
      progress: 7,
      total: 10,
      completed: false,
      reward: '250 XP + Oracle Title',
    },
    {
      id: 'ach5',
      name: 'Network Builder',
      description: 'Connect with 25 traders in the community',
      icon: <Users className="text-green-500" />,
      progress: 16,
      total: 25,
      completed: false,
      reward: '200 XP + Network Badge',
    },
    {
      id: 'ach6',
      name: 'Knowledge Seeker',
      description: 'Complete all beginner tutorials',
      icon: <BookOpen className="text-cyan-500" />,
      progress: 7,
      total: 7,
      completed: true,
      reward: '150 XP + Scholar Badge',
    }
  ];

  // Remove mock data for community feed posts
  const communityPosts: any[] = [];

  // Remove mock data for community members
  const communityMembers: any[] = [];

  // Remove mock data for upcoming events
  const upcomingEvents: any[] = [];

  // Create a function to parse privacy settings from messages
  const parseMessageContent = (message: ChatMessage): { 
    content: string, 
    shareWallet: boolean, 
    shareSocial: boolean 
  } => {
    if (!message.message.includes('||PRIVACY::')) {
      return { 
        content: message.message, 
        shareWallet: true, 
        shareSocial: true 
      };
    }
    
    try {
      const [content, privacyData] = message.message.split('||PRIVACY::');
      const [walletFlag, socialFlag] = privacyData.split('|');
      
      return {
        content,
        shareWallet: walletFlag === '1',
        shareSocial: socialFlag === '1'
      };
    } catch (e) {
      console.error('Error parsing message privacy data', e);
      return { 
        content: message.message, 
        shareWallet: true, 
        shareSocial: true 
      };
    }
  };

  // Update the shouldShowWalletAddress function 
  const shouldShowWalletAddress = (message: ChatMessage) => {
    const isOwnMessage = message.wallet_address === currentUserData.walletAddress;
    
    if (isOwnMessage) {
      return displayOwnWalletAddress;
    } else {
      // Check if the message has privacy metadata
      if (message.message.includes('||PRIVACY::')) {
        try {
          const privacyData = message.message.split('||PRIVACY::')[1];
          const walletFlag = privacyData.split('|')[0];
          return displayOthersWalletAddress && walletFlag === '1';
        } catch (e) {
          console.error('Error parsing privacy data', e);
          return displayOthersWalletAddress;
        }
      }
      return displayOthersWalletAddress;
    }
  };

  // Function to determine if social links should be shown
  const shouldShowSocialLinks = (message: ChatMessage) => {
    const isOwnMessage = message.wallet_address === currentUserData.walletAddress;
    
    if (isOwnMessage) {
      return true; // Always show our own social links to ourselves
    } else {
      // Check if the message has privacy metadata
      if (message.message.includes('||PRIVACY::')) {
        try {
          const privacyData = message.message.split('||PRIVACY::')[1];
          const socialFlag = privacyData.split('|')[1];
          return socialFlag === '1';
        } catch (e) {
          console.error('Error parsing privacy data', e);
          return true;
        }
      }
      return true; // Default to showing if no privacy metadata
    }
  };

  // Add state variable and interface for social media details
  const [socialMediaDetails, setSocialMediaDetails] = useState<Record<string, UserSocialMedia>>({});
  const [copiedDiscordId, setCopiedDiscordId] = useState<string | null>(null);
  
  // Fix the Twitter click handler
  const handleTwitterClick = (handle: string | null | undefined, messageId: string) => {
    const message = chatMessages.find(msg => msg.id === messageId);
    if (handle && message && shouldShowSocialLinks(message)) {
      window.open(`https://x.com/${handle}`, '_blank');
    }
  };
  
  // Fix the Telegram click handler
  const handleTelegramClick = (handle: string | null | undefined, messageId: string) => {
    const message = chatMessages.find(msg => msg.id === messageId);
    if (handle && message && shouldShowSocialLinks(message)) {
      window.open(`https://t.me/${handle}`, '_blank');
    }
  };
  
  // Fix the Discord handler
  const copyDiscordUsername = (username: string | null | undefined, messageId: string) => {
    const message = chatMessages.find(msg => msg.id === messageId);
    if (!username || !message || !shouldShowSocialLinks(message)) return;
    
    navigator.clipboard.writeText(username)
      .then(() => {
        // Show checkmark icon
        setCopiedDiscordId(messageId);
        
        // Hide after 2 seconds
        setTimeout(() => {
          setCopiedDiscordId(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };
  
  // Function to fetch user social media details
  const fetchUserSocialMedia = async (walletAddress: string) => {
    // Skip if we already have this user's details
    if (socialMediaDetails[walletAddress]) return;
    
    try {
      const profile = await getUserProfile(walletAddress);
      if (profile) {
        setSocialMediaDetails(prev => ({
          ...prev,
          [walletAddress]: {
            twitter_handle: profile.twitter_handle,
            telegram_handle: profile.telegram_handle,
            discord_handle: profile.discord_handle
          }
        }));
      }
    } catch (err) {
      console.error('Error fetching user social media details:', err);
    }
  };
  
  // Fetch social media details for visible chat messages
  useEffect(() => {
    if (chatMessages.length > 0) {
      const uniqueWallets = [...new Set(chatMessages.map(msg => msg.wallet_address))];
      uniqueWallets.forEach(wallet => {
        fetchUserSocialMedia(wallet);
      });
    }
  }, [chatMessages]);

  // Define state for tracking messages being deleted
  const [deletingMessageIds, setDeletingMessageIds] = useState<Set<string>>(new Set());

  // Add this function to delete messages
  const handleDeleteMessage = async (messageId: string) => {
    try {
      // Check if the message is within the deletable time window
      const message = chatMessages.find(msg => msg.id === messageId);
      if (!message) return;
      
      if (!canDeleteMessage(message.timestamp)) {
        console.error('Message is too old to delete');
        return;
      }
      
      // Check if this is the current user's message
      if (message.wallet_address !== currentUserData.walletAddress) {
        console.error('Cannot delete another user\'s message');
        return;
      }
      
      // Show a deleting state in the UI
      setDeletingMessageIds(prev => new Set(prev).add(messageId));
      
      // Delete completely from Supabase using adminSupabase to bypass RLS
      const { error } = await adminSupabase
        .from('chat_messages')
        .delete()
        .eq('id', messageId);
      
      if (error) {
        console.error('Error deleting message:', error);
        // Remove from deleting state
        setDeletingMessageIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
        return;
      }
      
      // Add to deleted message IDs to prevent reappearing
      setDeletedMessageIds(prev => new Set([...prev, messageId]));
      
      // Update local state to remove the message immediately
      setLocalMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
      
      // Remove from deleting state after a short delay
      setTimeout(() => {
        setDeletingMessageIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }, 500);
    } catch (error) {
      console.error('Error handling message deletion:', error);
      // Remove from deleting state
      setDeletingMessageIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  // Function to check if a message is within the 1-minute window
  const canDeleteMessage = (messageTimestamp: string): boolean => {
    const messageTime = new Date(messageTimestamp).getTime();
    const currentTime = new Date().getTime();
    const timeDifference = currentTime - messageTime;
    const oneMinuteInMs = 60 * 1000; // 1 minute in milliseconds
    
    return timeDifference <= oneMinuteInMs;
  };

  // Add state for community stats with string types (replacing previous implementation)
  const [communityStats, setCommunityStats] = useState({
    totalUsers: '0',
    newUsersToday: '0',
    marketCap: '$545k',
    holders: '2,522'
  });

  // Simple function to format numbers with commas
  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // New implementation for fetching community stats
  useEffect(() => {
    // Define the fetch function
    const fetchStats = async () => {
      try {
        
        // Get exact count of all users
        const { count: totalCount, error: countError } = await adminSupabase
          .from('users')
          .select('*', { count: 'exact', head: true });
          
        if (countError) {
          console.error("❌ Error fetching total user count:", countError);
          return;
        }
        
        // Get count of users created today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const { count: newToday, error: newTodayError } = await adminSupabase
          .from('users')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', today.toISOString());
        
        if (newTodayError) {
          console.error("❌ Error fetching new users count:", newTodayError);
          return;
        }
        
        // Update the state with formatted values
        setCommunityStats({
          totalUsers: totalCount !== null ? formatNumber(totalCount) : '0',
          newUsersToday: newToday !== null ? formatNumber(newToday) : '0',
          marketCap: '$545k',
          holders: '2,522'
        });
      } catch (err) {
        console.error("❌ Error in fetchStats:", err);
      }
    };
    
    // Run immediately
    fetchStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30 * 1000);
    
    // Cleanup
    return () => clearInterval(interval);
  }, []);

  // Track usernames mapped to wallet addresses
  const [walletToUsername, setWalletToUsername] = useState<Record<string, string>>({});

  // Function to get the current username for a wallet address
  const getCurrentUsername = (message: ChatMessage): string => {
    // If it's the current user, use their current name
    if (message.wallet_address === currentUserData.walletAddress && currentUserData.name) {
      return currentUserData.name;
    }
    
    // If we have a mapped username for this wallet, use that
    if (walletToUsername[message.wallet_address]) {
      return walletToUsername[message.wallet_address];
    }
    
    // Fallback to the original message user_name
    return message.user_name;
  };
  
  // Track all wallet addresses from messages and fetch their latest usernames
  useEffect(() => {
    const uniqueWallets = [...new Set(localMessages.map(msg => msg.wallet_address))];
    const fetchUsernames = async () => {
      const newWalletMap: Record<string, string> = { ...walletToUsername };
      let updated = false;
      
      for (const wallet of uniqueWallets) {
        if (wallet === 'system') continue; // Skip system messages
        
        try {
          const profile = await getUserProfile(wallet);
          if (profile?.display_name && profile.display_name !== newWalletMap[wallet]) {
            newWalletMap[wallet] = profile.display_name;
            updated = true;
          }
        } catch (error) {
          console.error(`Error fetching profile for ${wallet}:`, error);
        }
      }
      
      if (updated) {
        setWalletToUsername(newWalletMap);
      }
    };
    
    fetchUsernames();
  }, [localMessages]);

  return (
    <div className="community-container min-h-screen relative overflow-hidden">
      {/* Add custom styles */}
      <style>{communityStyles}</style>
      <style>{chatSettingsStyles}</style>
      
      {/* Network background animation */}
      <div className="bg-connections absolute inset-0 pointer-events-none" ref={connectionsRef}></div>
      
      {/* Main Content Section - Edge to Edge with fixed position */}
      <div className="w-full h-[calc(92.25vh-212px)] fixed top-[57px] left-0 right-0">
        {/* Main Content Row - Height calculated to meet bottom row */}
        <div className="grid grid-cols-7 gap-0 h-full">
          {/* Live Chat (2 columns) */}
          <div className="col-span-2 bg-[#182828] border border-[#42ae84]/20 rounded-none overflow-hidden h-full">
            <div className="h-full flex flex-col">
              <div className="p-2 border-b border-[#42ae84]/20 bg-[#1a2a2a] flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <MessageSquare size={16} className="text-[#42ae84]" />
                  <h2 className="text-sm font-semibold text-[#42ae84]">LIVE CHAT</h2>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-400">245 online</span>
                </div>
              </div>
              
              <div className="chat-container h-full flex-1">
                <div className="chat-messages community-scrollbar h-full flex-1 overflow-y-auto" 
                  ref={chatContainerRef}
                  onScroll={handleChatScroll}
                >
                  {isLoadingChat && <div className="system-message">Loading messages...</div>}
                  {chatError && <div className="system-message error">Error: {chatError}</div>}
                  
                  {localMessages.map(message => {
                    return message.message_type === 'system' ? (
                      <div key={message.id} className="system-message">
                        {message.message}
                      </div>
                    ) : (
                      <div key={message.id} className="chat-message">
                        <div className="chat-message-content">
                            <div className="chat-message-avatar">
                              <img 
                              src={message.avatar_url} 
                              alt={message.user_name} 
                                className="w-full h-full object-cover"
                              width="38"
                              height="38"
                              loading="lazy"
                              onError={(e) => {
                                console.error('Failed to load avatar:', message.avatar_url);
                                e.currentTarget.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png?auto=compress&cs=tinysrgb&w=100';
                              }}
                              />
                            </div>
                          <div className="chat-message-header">
                            <div className="user-info">
                              <div className="chat-username" style={{ color: message.user_color }}>
                                {getCurrentUsername(message)}
                              </div>
                              
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                {shouldShowWalletAddress(message) && (
                                  <span className="wallet-address">
                                    {`${message.wallet_address.slice(0, 4)}...${message.wallet_address.slice(-4)}`}
                                    <button 
                                      className="copy-button"
                                      onClick={() => copyWalletAddress(message.wallet_address, message.id)}
                                      title="Copy wallet address"
                                    >
                                      {showCheckmarks[message.id] ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="check-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                      ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="copy-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                      )}
                                    </button>
                                  </span>
                                )}
                                
                                {/* Twitter/X Icon */}
                                <div 
                                  className={`social-icon twitter-icon ${
                                    socialMediaDetails[message.wallet_address]?.twitter_handle && 
                                    shouldShowSocialLinks(message)
                                      ? 'active' 
                                      : 'inactive'
                                  }`}
                                  title={socialMediaDetails[message.wallet_address]?.twitter_handle ? `X: @${socialMediaDetails[message.wallet_address]?.twitter_handle}` : 'X not linked'}
                                  onClick={() => handleTwitterClick(socialMediaDetails[message.wallet_address]?.twitter_handle, message.id)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" fill="currentColor" />
                                  </svg>
                                </div>
                                
                                {/* Telegram Icon */}
                                <div 
                                  className={`social-icon telegram-icon ${
                                    socialMediaDetails[message.wallet_address]?.telegram_handle && 
                                    shouldShowSocialLinks(message)
                                      ? 'active' 
                                      : 'inactive'
                                  }`}
                                  title={socialMediaDetails[message.wallet_address]?.telegram_handle && shouldShowSocialLinks(message) ? `Telegram: @${socialMediaDetails[message.wallet_address]?.telegram_handle}` : 'Telegram not linked'}
                                  onClick={() => handleTelegramClick(socialMediaDetails[message.wallet_address]?.telegram_handle, message.id)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-1.515 7.143c-.112.54-.412.674-.836.42l-2.302-1.7-1.112 1.072c-.125.125-.23.23-.468.23l.167-2.378 4.332-3.913c.188-.168-.041-.262-.292-.094L7.48 12.77l-2.295-.714c-.5-.157-.508-.5.104-.74l8.964-3.454c.431-.157.81.104.662.386z" fill="currentColor" />
                                  </svg>
                                </div>
                                
                                {/* Discord Icon */}
                                <div 
                                  className={`social-icon discord-icon ${socialMediaDetails[message.wallet_address]?.discord_handle && shouldShowSocialLinks(message) ? 'active' : 'inactive'}`}
                                  title={socialMediaDetails[message.wallet_address]?.discord_handle && shouldShowSocialLinks(message) ? `Discord: ${socialMediaDetails[message.wallet_address]?.discord_handle}` : 'Discord not linked'}
                                  onClick={() => copyDiscordUsername(socialMediaDetails[message.wallet_address]?.discord_handle, message.id)}
                                >
                                  {copiedDiscordId === message.id ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z" fill="currentColor" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="timestamp flex items-center gap-2">
                                <span>{formatMessageTime(message.timestamp)}</span>
                                
                                {/* Delete button - only show for own messages within 1-minute window */}
                                {message.wallet_address === currentUserData.walletAddress && canDeleteMessage(message.timestamp) && (
                                  <button 
                                    className={`text-gray-400 hover:text-red-400 transition-colors ${deletingMessageIds.has(message.id) ? 'opacity-50' : ''}`}
                                    onClick={() => handleDeleteMessage(message.id)}
                                    title="Delete message (available for 1 minute)"
                                    disabled={deletingMessageIds.has(message.id)}
                                  >
                                    {deletingMessageIds.has(message.id) ? (
                                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    ) : (
                                      <Trash2 size={14} />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="chat-message-body">
                            <div className="message-text">
                              {message.message.includes('||PRIVACY::') 
                               ? message.message.split('||PRIVACY::')[0] 
                               : message.message}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="chat-input-container">
                  <div className="chat-input-wrapper">
                    <input
                      type="text"
                      className="chat-input"
                      placeholder="Type a message..."
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      ref={chatInputRef}
                    />
                    <div className="chat-actions">
                      <div className="chat-action-btn relative" onClick={toggleEmojiPicker}>
                        <Smile size={16} />
                        {showEmojiPicker && (
                          <div className="emoji-picker-container" ref={emojiPickerRef}>
                            <div className="bg-[#1a2a2a] rounded-md p-3 border border-[#42ae84]/20 w-[320px]">
                              <div className="flex flex-wrap gap-2 justify-center">
                                {['😀', '😂', '😍', '😊', '🙂', '😎', '🤔', '😆', '🥰', '😇', '😜', '😅', '👍', '👏', '🔥', '❤️', '🎉', '💯', '✨', '🚀', '💪', '🤝', '👌', '💰', '🏆'].map(emoji => (
                                  <div 
                                    key={emoji} 
                                    className="w-11 h-11 flex items-center justify-center cursor-pointer hover:bg-[#243434] rounded-md text-2xl"
                                    onClick={() => handleEmojiSelect(emoji)}
                                  >
                                    {emoji}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="chat-action-btn" onClick={toggleChatSettings}>
                        <Settings size={16} />
                      </div>
                      
                      <button 
                        className="ml-1 p-1.5 rounded bg-[#42ae84] text-white"
                        onClick={handleSendMessage}
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Community Tabs (3 columns) */}
          <div className="col-span-3 bg-[#182828] border-t border-b border-[#42ae84]/20 rounded-none overflow-hidden h-full flex flex-col">
            <div className="border-b border-[#42ae84]/20 bg-[#1a2a2a]">
              <div className="flex">
                <button 
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${activeTab === 'feed' ? 'bg-[#42ae84]/10 text-[#42ae84] border-b-2 border-[#42ae84]' : 'text-gray-400'}`}
                  onClick={() => handleTabClick('feed')}
                >
                  <Globe size={14} />
                  Feed
                </button>
                <button 
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${activeTab === 'members' ? 'bg-[#42ae84]/10 text-[#42ae84] border-b-2 border-[#42ae84]' : 'text-gray-400'}`}
                  onClick={() => handleTabClick('members')}
                >
                  <Users size={14} />
                  Members
                        </button>
                <button 
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${activeTab === 'events' ? 'bg-[#42ae84]/10 text-[#42ae84] border-b-2 border-[#42ae84]' : 'text-gray-400'}`}
                  onClick={() => handleTabClick('events')}
                >
                  <Calendar size={14} />
                  Events
                        </button>
                  </div>
                </div>
                
            <div className="flex-1 overflow-y-auto community-scrollbar p-3">
              {activeTab === 'feed' && (
                <div className="space-y-3">
                {communityPosts.length > 0 ? (
                  communityPosts.map(post => (
                    <div key={post.id} className="bg-[#1c2c2c] border border-[#42ae84]/10 rounded-lg overflow-hidden">
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-10 h-10 rounded-full overflow-hidden">
                          <img 
                            src={post.user.avatar} 
                            alt={post.user.name} 
                            className="w-full h-full object-cover" 
                            onError={(e) => {
                              e.currentTarget.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png?auto=compress&cs=tinysrgb&w=100';
                            }}
                          />
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5">
                            <h3 className="text-sm font-medium">{post.user.name}</h3>
                              <div className="px-1.5 py-0.5 bg-[#42ae84]/10 rounded text-[#42ae84] text-xs font-medium">
                              {post.user.badge}
                            </div>
                          </div>
                            <div className="text-xs text-gray-400">{post.time}</div>
                        </div>
                      </div>
                        <p className="text-sm text-gray-300 mb-3">
                          {post.content}
                        </p>
                        <div className="flex items-center gap-3">
                          <button className={`text-xs flex items-center gap-1 ${post.isLiked ? 'text-[#42ae84]' : 'text-gray-400'}`}>
                            <Heart size={14} className={post.isLiked ? 'fill-[#42ae84]' : ''} />
                            {post.likes}
                          </button>
                          <button className="text-xs flex items-center gap-1 text-gray-400">
                            <MessageCircle size={14} />
                            {post.comments}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                    <MessageSquare size={48} className="text-[#42ae84]/30 mb-3" />
                    <p className="text-lg font-medium">No posts yet</p>
                    <p className="text-sm">Be the first to share with the community</p>
                  </div>
                )}
                </div>
              )}
            
            {activeTab === 'members' && (
                <div className="space-y-3">
                  {communityMembers.length > 0 ? (
                    communityMembers.map(member => (
                    <div key={member.id} className="bg-[#1c2c2c] border border-[#42ae84]/10 rounded-lg overflow-hidden">
                      <div className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-full overflow-hidden">
                              <img 
                                src={member.avatar} 
                                alt={member.name} 
                                className="w-full h-full object-cover" 
                                onError={(e) => {
                                  e.currentTarget.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png?auto=compress&cs=tinysrgb&w=100';
                                }}
                              />
                            </div>
                            <div>
                            <div className="flex items-center gap-1.5">
                                <h3 className="text-sm font-medium">{member.name}</h3>
                              <div className="px-1.5 py-0.5 bg-[#42ae84]/10 rounded text-[#42ae84] text-xs font-medium">
                                  {member.badge}
                                </div>
                              </div>
                            <div className="text-xs text-gray-400">{member.role}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-gray-400">{member.followers} followers</div>
                            <button className={`px-2 py-1 rounded text-xs ${member.isFollowing ? 'bg-[#42ae84]/20 text-[#42ae84] border border-[#42ae84]/30' : 'bg-[#1c2c2c] border border-gray-700 text-gray-300'}`}>
                              {member.isFollowing ? 'Following' : 'Follow'}
                            </button>
                        </div>
                      </div>
                    </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                      <Users size={48} className="text-[#42ae84]/30 mb-3" />
                      <p className="text-lg font-medium">No members available</p>
                      <p className="text-sm">The community is still growing</p>
                    </div>
                  )}
                </div>
              )}
            
            {activeTab === 'events' && (
                <div className="space-y-3">
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map(event => (
                      <div key={event.id} className="bg-[#1c2c2c] border border-[#42ae84]/10 rounded-lg overflow-hidden">
                        <div className="p-3">
                          <h3 className="text-sm font-medium mb-1">{event.title}</h3>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="text-xs flex items-center gap-1 text-gray-400">
                              <Calendar size={14} className="text-[#42ae84]" />
                              {event.date}
                            </div>
                            <div className="text-xs flex items-center gap-1 text-gray-400">
                              <Clock size={14} className="text-[#42ae84]" />
                              {event.time}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-400">
                              {event.attendees} attendees
                            </div>
                            <button 
                              className={`px-2 py-1 rounded text-xs ${
                                event.isRegistered 
                                  ? 'bg-[#42ae84]/20 text-[#42ae84] border border-[#42ae84]/30' 
                                  : 'bg-[#42ae84]/10 border border-[#42ae84]/30 text-[#42ae84]'
                              }`}
                            >
                              {event.isRegistered ? 'Registered' : 'Register'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                      <Calendar size={48} className="text-[#42ae84]/30 mb-3" />
                      <p className="text-lg font-medium">No upcoming events</p>
                      <p className="text-sm">Check back later for new events</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Leaderboards (2 columns) */}
          <div className="col-span-2 bg-[#182828] border border-[#42ae84]/20 rounded-none overflow-hidden h-full flex flex-col">
            <div className="p-2 border-b border-[#42ae84]/20 bg-[#1a2a2a] flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Award size={16} className="text-[#42ae84]" />
                <h2 className="text-sm font-semibold text-[#42ae84]">LEADERBOARDS</h2>
              </div>
              {showAchievements && (
                <button 
                  className="text-xs text-gray-400 hover:text-gray-300"
                  onClick={() => setShowAchievements(false)}
                >
                  Back to Rankings
                    </button>
              )}
              {!showAchievements && (
                <button 
                  className="text-xs text-[#42ae84] hover:underline"
                  onClick={() => setShowAchievements(true)}
                >
                  View Achievements
                </button>
              )}
                  </div>
            
            {!showAchievements && (
              <div className="flex-1 flex flex-col">
                <div className="p-2 border-b border-[#42ae84]/10 bg-[#1a2a2a]">
                  <div className="flex space-x-1">
                    <button 
                      className={`leaderboard-tab ${activeLeaderboard === 'trading' ? 'active' : ''}`}
                      onClick={() => setActiveLeaderboard('trading')}
                    >
                      Trading
                    </button>
                    <button 
                      className={`leaderboard-tab ${activeLeaderboard === 'community' ? 'active' : ''}`}
                      onClick={() => setActiveLeaderboard('community')}
                    >
                      Community
                    </button>
                </div>
                </div>
                
                <div className="flex-1 overflow-y-auto community-scrollbar p-3">
                  {activeLeaderboard === 'trading' && (
                    <div className="space-y-2">
                      {tradingLeaderboard.length > 0 ? (
                        tradingLeaderboard.map((user, index) => (
                          <div key={user.id} className="leaderboard-item">
                            <div className={`leaderboard-rank mr-2 ${
                              index === 0 ? 'rank-1' : index === 1 ? 'rank-2' : index === 2 ? 'rank-3' : 'rank-other'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 flex items-center">
                              <div className="w-8 h-8 rounded-full overflow-hidden mr-2">
                                <img 
                                  src={user.avatar} 
                                  alt={user.name} 
                                  className="w-full h-full object-cover" 
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png?auto=compress&cs=tinysrgb&w=100';
                                  }}
                                />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm font-medium">{user.name}</div>
                                  <div className="level-badge">Lvl {user.level}</div>
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                                  <div>Win Rate: {user.winRate}</div>
                                  <div className="text-green-400">{user.profit}</div>
                                </div>
                                <div className="progress-bar w-full">
                                  <div 
                                    className="progress-fill" 
                                    style={{ width: `${calculateLevelProgress(user.xp, user.nextLevel)}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                          <Trophy size={48} className="text-[#42ae84]/30 mb-3" />
                          <p className="text-lg font-medium">No trading leaderboard data</p>
                          <p className="text-sm">Check back soon for rankings</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {activeLeaderboard === 'community' && (
                    <div className="space-y-2">
                      {communityLeaderboard.length > 0 ? (
                        communityLeaderboard.map((user, index) => (
                          <div key={user.id} className="leaderboard-item">
                            <div className={`leaderboard-rank mr-2 ${
                              index === 0 ? 'rank-1' : index === 1 ? 'rank-2' : index === 2 ? 'rank-3' : 'rank-other'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 flex items-center">
                              <div className="w-8 h-8 rounded-full overflow-hidden mr-2">
                                <img 
                                  src={user.avatar} 
                                  alt={user.name} 
                                  className="w-full h-full object-cover" 
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png?auto=compress&cs=tinysrgb&w=100';
                                  }}
                                />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="text-sm font-medium">{user.name}</div>
                                  <div className="level-badge">Lvl {user.level}</div>
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                                  <div>{user.contributions} contributions</div>
                                  <div className="text-orange-400">{user.streak}</div>
                                </div>
                                <div className="progress-bar w-full">
                                  <div 
                                    className="progress-fill" 
                                    style={{ width: `${calculateLevelProgress(user.xp, user.nextLevel)}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                          <Users size={48} className="text-[#42ae84]/30 mb-3" />
                          <p className="text-lg font-medium">No community leaderboard data</p>
                          <p className="text-sm">Community contributions will appear here</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {showAchievements && (
              <div className="flex-1 overflow-y-auto community-scrollbar p-3">
                <h3 className="text-sm font-medium mb-3">Your Achievements</h3>
                <div className="grid grid-cols-2 gap-3">
                  {achievements.map(achievement => (
                    <div 
                      key={achievement.id} 
                      className={`trophy-card ${!achievement.completed ? 'trophy-locked' : ''}`}
                    >
                      <div className="trophy-icon">
                        {achievement.icon}
              </div>
                      <div className="text-xs font-medium mb-1">{achievement.name}</div>
                      <div className="text-xs text-gray-400 mb-2">{achievement.description}</div>
                      <div className="text-xs text-gray-400">
                        {achievement.progress}/{achievement.total}
                      </div>
                      {!achievement.completed && (
                        <div 
                          className="achievement-progress" 
                          style={{ width: `${(achievement.progress / achievement.total) * 100}%` }}
                        ></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
              </div>
            </div>
            
      {/* Bottom Row - Full Width Edge to Edge */}
      <div className="w-full fixed bottom-0 left-0 right-0">
        <div className="grid grid-cols-6 gap-0">
          {/* Trading Resources */}
          <div className="community-card rounded-none border-r border-t border-[#42ae84]/20">
              <div className="community-card-header">
                <h2 className="text-sm font-semibold text-[#42ae84]">TRADING RESOURCES</h2>
              </div>
            <div className="community-card-body p-3 h-[180px] overflow-y-auto community-scrollbar">
              <div className="space-y-2 pb-4">
                <div className="flex items-center gap-2 p-2 bg-[#1c2c2c] rounded hover:bg-[#243434] cursor-pointer">
                        <BookOpen size={16} className="text-[#42ae84]" />
                  <div className="flex-1">
                    <div className="text-xs font-medium">Trading Guide: Chart Patterns</div>
                    <div className="text-xs text-gray-400">15 min read</div>
                      </div>
                      </div>
                <div className="flex items-center gap-2 p-2 bg-[#1c2c2c] rounded hover:bg-[#243434] cursor-pointer">
                  <FileText size={16} className="text-[#42ae84]" />
                  <div className="flex-1">
                    <div className="text-xs font-medium">Risk Management Essentials</div>
                    <div className="text-xs text-gray-400">8 min read</div>
                    </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-[#1c2c2c] rounded hover:bg-[#243434] cursor-pointer">
                  <Rocket size={16} className="text-[#42ae84]" />
                  <div className="flex-1">
                    <div className="text-xs font-medium">Advanced Trading Strategies</div>
                    <div className="text-xs text-gray-400">20 min read</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-[#1c2c2c] rounded hover:bg-[#243434] cursor-pointer">
                  <BarChart2 size={16} className="text-[#42ae84]" />
                  <div className="flex-1">
                    <div className="text-xs font-medium">Technical Analysis Fundamentals</div>
                    <div className="text-xs text-gray-400">12 min read</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-[#1c2c2c] rounded hover:bg-[#243434] cursor-pointer">
                  <Shield size={16} className="text-[#42ae84]" />
                  <div className="flex-1">
                    <div className="text-xs font-medium">Protecting Your Crypto Assets</div>
                    <div className="text-xs text-gray-400">10 min read</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Community Stats */}
          <div className="community-card rounded-none border-r border-t border-[#42ae84]/20">
            <div className="community-card-header">
              <h2 className="text-sm font-semibold text-[#42ae84]">EKKO STATS</h2>
                      </div>
            <div className="community-card-body p-3 h-[180px]">
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-[#1c2c2c] rounded">
                  <div className="text-xs text-gray-400 mb-1">Active Members</div>
                  <div className="text-lg font-semibold text-white">{communityStats.totalUsers}</div>
                      </div>
                <div className="p-2 bg-[#1c2c2c] rounded">
                  <div className="text-xs text-gray-400 mb-1">New Today</div>
                  <div className="text-lg font-semibold text-green-400">+{communityStats.newUsersToday}</div>
                    </div>
                <div className="p-2 bg-[#1c2c2c] rounded">
                  <div className="text-xs text-gray-400 mb-1">$EKKO MC</div>
                  <div className="text-lg font-semibold text-white">{communityStats.marketCap}</div>
                </div>
                <div className="p-2 bg-[#1c2c2c] rounded">
                  <div className="text-xs text-gray-400 mb-1">Holders</div>
                  <div className="text-lg font-semibold text-white">{communityStats.holders}</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Trending Topics Card */}
          <div className="community-card rounded-none border-r border-t border-[#42ae84]/20">
            <div className="community-card-header">
              <h2 className="text-sm font-semibold text-[#42ae84]">TRENDING TOPICS</h2>
                      </div>
            <div className="community-card-body p-3 h-[180px] overflow-y-auto community-scrollbar">
              <div className="space-y-2 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <Hash size={14} className="text-[#42ae84]" />
                    <span className="text-xs font-medium">ETHMerge</span>
                      </div>
                  <div className="text-xs text-gray-400">243 posts</div>
                    </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <Hash size={14} className="text-[#42ae84]" />
                    <span className="text-xs font-medium">BearMarketStrategies</span>
                  </div>
                  <div className="text-xs text-gray-400">187 posts</div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <Hash size={14} className="text-[#42ae84]" />
                    <span className="text-xs font-medium">DEXTrading</span>
                  </div>
                  <div className="text-xs text-gray-400">156 posts</div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <Hash size={14} className="text-[#42ae84]" />
                    <span className="text-xs font-medium">NFTCollections</span>
                  </div>
                  <div className="text-xs text-gray-400">142 posts</div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <Hash size={14} className="text-[#42ae84]" />
                    <span className="text-xs font-medium">MetaverseProjects</span>
                  </div>
                  <div className="text-xs text-gray-400">118 posts</div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <Hash size={14} className="text-[#42ae84]" />
                    <span className="text-xs font-medium">LayerTwoScaling</span>
                  </div>
                  <div className="text-xs text-gray-400">104 posts</div>
                </div>
              </div>
            </div>
                </div>
                
          {/* Community Alert */}
          <div className="community-card rounded-none border-r border-t border-[#c19411]/30 bg-[#1c2c2c]">
            <div className="p-4 h-[212px] flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Bell size={16} className="text-[#c19411]" />
                <h3 className="text-sm font-semibold text-[#c19411]">COMMUNITY ANNOUNCEMENT</h3>
              </div>
              <p className="text-sm text-gray-300 mb-5">
                Join our weekly trading workshop this Friday at 2PM UTC where we'll discuss advanced chart pattern recognition.
              </p>
              <button className="w-full mb-4 py-2 text-xs font-medium bg-[#c19411]/10 hover:bg-[#c19411]/20 transition-colors text-[#c19411] border border-[#c19411]/30 rounded-md mt-auto">
                RSVP to Event
                </button>
              </div>
            </div>
            
          {/* Active Members */}
          <div className="community-card rounded-none border-r border-t border-[#42ae84]/20">
              <div className="community-card-header">
              <h2 className="text-sm font-semibold text-[#42ae84]">ACTIVE MEMBERS</h2>
              </div>
            <div className="community-card-body p-3 h-[180px] overflow-y-auto community-scrollbar">
              {communityMembers.length > 0 ? (
                <div className="space-y-2 pb-4">
                  {communityMembers.map(member => (
                    <div key={member.id} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <div className="w-7 h-7 rounded-full overflow-hidden">
                            <img 
                              src={member.avatar} 
                              alt={member.name} 
                              className="w-full h-full object-cover" 
                              onError={(e) => {
                                e.currentTarget.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png?auto=compress&cs=tinysrgb&w=100';
                              }}
                            />
                    </div>
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border border-[#182828]"></div>
                    </div>
                        <div className="text-xs font-medium">{member.name}</div>
                    </div>
                      <div className="text-xs text-[#42ae84]">{member.role}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  No active members
                </div>
              )}
              </div>
            </div>
          
          {/* Top Traders This Week */}
          <div className="community-card rounded-none border-t border-[#42ae84]/20">
            <div className="community-card-header">
              <h2 className="text-sm font-semibold text-[#42ae84]">TOP TRADERS THIS WEEK</h2>
          </div>
            <div className="community-card-body p-3 h-[180px] overflow-y-auto community-scrollbar">
              {tradingLeaderboard.length > 0 ? (
                <div className="space-y-2 pb-4">
                  {tradingLeaderboard.map((trader, index) => (
                    <div key={trader.id} className="flex items-center gap-2 py-1">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-yellow-500 text-yellow-900' : 
                        index === 1 ? 'bg-gray-300 text-gray-800' : 
                        'bg-amber-700 text-amber-100'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="w-6 h-6 rounded-full overflow-hidden">
                        <img 
                          src={trader.avatar} 
                          alt={trader.name} 
                          className="w-full h-full object-cover" 
                          onError={(e) => {
                            e.currentTarget.src = 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png?auto=compress&cs=tinysrgb&w=100';
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-medium">{trader.name}</div>
                        <div className="text-xs text-green-400">{trader.profit}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  No top traders data
                </div>
              )}
              </div>
            </div>
        </div>
      </div>
      
      {/* Settings Modal */}
      {showChatSettings && (
        <div className="settings-backdrop visible" onClick={() => setShowChatSettings(false)}>
          <div className="chat-settings-dropdown" ref={chatSettingsRef} onClick={e => e.stopPropagation()}>
            <div className="p-2 border-b border-[#42ae84]/20 bg-[#1a2a2a] flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Settings size={16} className="text-[#42ae84]" />
                <h2 className="text-sm font-semibold text-[#42ae84]">CHAT SETTINGS</h2>
              </div>
              <button 
                className="p-1 text-gray-400 hover:text-white rounded-full" 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowChatSettings(false);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="settings-container p-2">
              {/* Column 1 */}
              <div className="settings-column">
                <div className="chat-settings-section">
                  <div className="chat-settings-header">Display Settings</div>
                  <div className="chat-settings-item">
                    <div className="chat-settings-label">Show my wallet address</div>
                    <button 
                      className={`w-10 h-5 rounded-full relative focus:outline-none ${displayOwnWalletAddress ? 'bg-[#42ae84]' : 'bg-gray-600'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOwnWalletDisplayToggle();
                      }}
                    >
                      <div className={`absolute w-4 h-4 rounded-full bg-white transition-all transform ${displayOwnWalletAddress ? 'translate-x-5' : 'translate-x-0.5'} top-0.5`}></div>
                    </button>
                  </div>
                  <div className="chat-settings-item">
                    <div className="chat-settings-label">Show others' wallet addresses</div>
                    <button 
                      className={`w-10 h-5 rounded-full relative focus:outline-none ${displayOthersWalletAddress ? 'bg-[#42ae84]' : 'bg-gray-600'}`}
                      onClick={handleOthersWalletDisplayToggle}
                    >
                      <div className={`absolute w-4 h-4 rounded-full bg-white transition-all transform ${displayOthersWalletAddress ? 'translate-x-5' : 'translate-x-0.5'} top-0.5`}></div>
                    </button>
                  </div>
                </div>
                
                <div className="chat-settings-section">
                  <div className="chat-settings-header">Privacy Settings</div>
                  <div className="chat-settings-item">
                    <div className="chat-settings-label">Share my wallet address</div>
                    <button 
                      className={`w-10 h-5 rounded-full relative focus:outline-none ${shareWalletWithOthers ? 'bg-[#42ae84]' : 'bg-gray-600'}`}
                      onClick={handleShareWalletToggle}
                    >
                      <div className={`absolute w-4 h-4 rounded-full bg-white transition-all transform ${shareWalletWithOthers ? 'translate-x-5' : 'translate-x-0.5'} top-0.5`}></div>
                    </button>
                  </div>
                  <div className="chat-settings-item">
                    <div className="chat-settings-label">Share my social links</div>
                    <button 
                      className={`w-10 h-5 rounded-full relative focus:outline-none ${shareSocialLinks ? 'bg-[#42ae84]' : 'bg-gray-600'}`}
                      onClick={handleShareSocialLinksToggle}
                    >
                      <div className={`absolute w-4 h-4 rounded-full bg-white transition-all transform ${shareSocialLinks ? 'translate-x-5' : 'translate-x-0.5'} top-0.5`}></div>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Column 2 */}
              <div className="settings-column">
                <div className="chat-settings-section">
                  <div className="chat-settings-header">Notifications</div>
                  <div className="chat-settings-item">
                    <div className="chat-settings-label">Sound notifications</div>
                    <button
                      className={`w-10 h-5 rounded-full relative focus:outline-none ${notificationSound ? 'bg-[#42ae84]' : 'bg-gray-600'}`}
                      onClick={handleNotificationSoundToggle}
                    >
                      <div className={`absolute w-4 h-4 rounded-full bg-white transition-all transform ${notificationSound ? 'translate-x-5' : 'translate-x-0.5'} top-0.5`}></div>
                    </button>
                  </div>
                  <div className="chat-settings-item">
                    <div className="chat-settings-label">Email notifications</div>
                    <button
                      className={`w-10 h-5 rounded-full relative focus:outline-none ${emailNotifications ? 'bg-[#42ae84]' : 'bg-gray-600'}`}
                      onClick={handleEmailNotificationsToggle}
                    >
                      <div className={`absolute w-4 h-4 rounded-full bg-white transition-all transform ${emailNotifications ? 'translate-x-5' : 'translate-x-0.5'} top-0.5`}></div>
                    </button>
                  </div>
                </div>
                
                <div className="chat-settings-section">
                  <div className="chat-settings-header">Your Chat Color</div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {['#42ae84', '#4287f5', '#e84393', '#00b894', '#9b59b6', '#f39c12'].map(color => (
                      <div 
                        key={color}
                        className={`color-option ${userColor === color ? 'selected' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => handleColorSelect(color)}
                      ></div>
                    ))}
                  </div>
                </div>
                
                <div className="chat-settings-section">
                  <div className="chat-settings-header">Your Status</div>
                  <div className="flex flex-col gap-1 mt-2">
                    {[
                      { id: 'online' as const, label: 'Online', icon: <Volume2 size={14} className="text-green-500" /> },
                      { id: 'away' as const, label: 'Away', icon: <Volume size={14} className="text-yellow-500" /> },
                      { id: 'busy' as const, label: 'Do Not Disturb', icon: <Volume size={14} className="text-red-500" /> },
                    ].map(status => (
                      <div 
                        key={status.id}
                        className={`flex items-center gap-2 p-1.5 rounded cursor-pointer ${userStatusLocal === status.id ? 'bg-[#42ae84]/10' : ''}`}
                        onClick={() => handleUserStatusChange(status.id)}
                      >
                        {status.icon}
                        <span className="text-sm">{status.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Community;